import type { DifficultyKey } from '../game/balance';
import type { GameHapticEvent, World } from '../game/world';

export const BRIDGE_VERSION = 2;

export type LevelId = 'base_lab' | 'expansion_1' | 'expansion_2';

export type RunConfig = {
  runId: string;
  issuedAt: number;
  seed: number;
  levelId: LevelId;
  difficulty: DifficultyKey;
};

export type NativeCapabilities = {
  gameCenterAvailable: boolean;
  purchasesAvailable: boolean;
};

export type NativeSettings = {
  musicEnabled: boolean;
  hapticsEnabled: boolean;
};

export type { GameHapticEvent } from '../game/world';

export type Entitlements = {
  premium: boolean;
};

type RunFlags = {
  debug: boolean;
  allAbilities: boolean;
  hiddenVariant: boolean;
};

export type ScorePayload = {
  runId: string;
  score: number;
  seconds: number;
  level: number;
  levelId: LevelId;
  difficulty: DifficultyKey;
  victory: boolean;
  bossKills: number;
  playerLevel: number;
  seed: number;
  debug: boolean;
  allAbilities: boolean;
  hiddenVariant: boolean;
};

/** Minimal, finished-run data that the native shell may place in an OS share sheet. */
export type ScoreSharePayload = {
  runId: string;
  score: number;
  seconds: number;
  playerLevel: number;
  levelId: LevelId;
  victory: boolean;
};

export type WebToNativeMessage =
  | {
      version: typeof BRIDGE_VERSION;
      type: 'web.ready';
      payload: {
        href: string;
        debug: boolean;
        hiddenVariant: boolean;
      };
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'web.error';
      payload: {
        message: string;
        stack?: string;
      };
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'game.started';
      payload: {
        run: RunConfig;
        flags: RunFlags;
      };
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'game.paused';
      payload: Record<string, never>;
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'game.over';
      payload: ScorePayload;
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'game.requestRestart';
      payload: {
        runId: string;
      };
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'game.requestExit';
      payload: {
        runId: string;
      };
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'game.requestLeaderboard';
      payload: Record<string, never>;
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'game.shareScore';
      payload: ScoreSharePayload;
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'game.haptic';
      payload: {
        event: GameHapticEvent;
      };
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'game.requestSettingsUpdate';
      payload: {
        settings: NativeSettings;
      };
    };

export type NativeToWebMessage =
  | {
      version: typeof BRIDGE_VERSION;
      type: 'native.bootstrap';
      payload: {
        run: RunConfig;
        capabilities: NativeCapabilities;
        settings: NativeSettings;
        entitlements: Entitlements;
      };
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'native.startRun';
      payload: {
        run: RunConfig;
      };
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'native.pause';
      payload: Record<string, never>;
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'native.resume';
      payload: Record<string, never>;
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'native.settingsUpdated';
      payload: {
        settings: NativeSettings;
      };
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'native.entitlementsUpdated';
      payload: {
        entitlements: Entitlements;
      };
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'native.scoreResult';
      payload: {
        runId: string;
        submitted: boolean;
        reason?: string;
      };
    }
  | {
      version: typeof BRIDGE_VERSION;
      type: 'native.error';
      payload: {
        message: string;
      };
    };

type ReactNativeWebView = {
  postMessage(message: string): void;
};

type BridgeWindow = Window & {
  ReactNativeWebView?: ReactNativeWebView;
  __BREWMANCER_NATIVE_BRIDGE__?: {
    receive(message: NativeToWebMessage | string): void;
  };
};

export type NativeBridgeHandlers = {
  onBootstrap?(payload: Extract<NativeToWebMessage, { type: 'native.bootstrap' }>['payload']): void;
  onStartRun?(run: RunConfig): void;
  onPause?(): void;
  onResume?(): void;
  onSettingsUpdated?(settings: NativeSettings): void;
  onEntitlementsUpdated?(entitlements: Entitlements): void;
  onScoreResult?(payload: Extract<NativeToWebMessage, { type: 'native.scoreResult' }>['payload']): void;
};

let activeRun: RunConfig | null = null;
let handlers: NativeBridgeHandlers | null = null;
let queuedBootstrap: Extract<NativeToWebMessage, { type: 'native.bootstrap' }>['payload'] | null = null;
let initialized = false;

function getWindow(): BridgeWindow | null {
  return typeof window === 'undefined' ? null : (window as BridgeWindow);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isDifficulty(value: unknown): value is DifficultyKey {
  return value === 'normal' || value === 'easy';
}

function isLevelId(value: unknown): value is LevelId {
  return value === 'base_lab' || value === 'expansion_1' || value === 'expansion_2';
}

function isRunConfig(value: unknown): value is RunConfig {
  if (!isRecord(value)) return false;
  return isString(value.runId) && isNumber(value.issuedAt) && isNumber(value.seed) && isLevelId(value.levelId) && isDifficulty(value.difficulty);
}

function isCapabilities(value: unknown): value is NativeCapabilities {
  return isRecord(value) && typeof value.gameCenterAvailable === 'boolean' && typeof value.purchasesAvailable === 'boolean';
}

function isSettings(value: unknown): value is NativeSettings {
  return isRecord(value) && typeof value.musicEnabled === 'boolean' && typeof value.hapticsEnabled === 'boolean';
}

function isEntitlements(value: unknown): value is Entitlements {
  return isRecord(value) && typeof value.premium === 'boolean';
}

function isEmptyPayload(value: unknown): value is Record<string, never> {
  return isRecord(value) && Object.keys(value).length === 0;
}

export function parseNativeMessage(raw: NativeToWebMessage | string): NativeToWebMessage | null {
  try {
    const data: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!isRecord(data) || data.version !== BRIDGE_VERSION || typeof data.type !== 'string' || !isRecord(data.payload)) return null;

    switch (data.type) {
      case 'native.bootstrap':
        if (!isRunConfig(data.payload.run) || !isCapabilities(data.payload.capabilities) || !isSettings(data.payload.settings) || !isEntitlements(data.payload.entitlements)) return null;
        return data as NativeToWebMessage;
      case 'native.startRun':
        return isRunConfig(data.payload.run) ? (data as NativeToWebMessage) : null;
      case 'native.pause':
      case 'native.resume':
        return isEmptyPayload(data.payload) ? (data as NativeToWebMessage) : null;
      case 'native.settingsUpdated':
        return isSettings(data.payload.settings) ? (data as NativeToWebMessage) : null;
      case 'native.entitlementsUpdated':
        return isEntitlements(data.payload.entitlements) ? (data as NativeToWebMessage) : null;
      case 'native.scoreResult':
        return isString(data.payload.runId) && typeof data.payload.submitted === 'boolean' && (data.payload.reason === undefined || typeof data.payload.reason === 'string')
          ? (data as NativeToWebMessage)
          : null;
      case 'native.error':
        return isString(data.payload.message) ? (data as NativeToWebMessage) : null;
      default:
        return null;
    }
  } catch {
    return null;
  }
}

function receiveNativeMessage(raw: NativeToWebMessage | string): void {
  const message = parseNativeMessage(raw);
  if (message === null) return;

  if (message.type === 'native.bootstrap') {
    if (handlers?.onBootstrap) handlers.onBootstrap(message.payload);
    else queuedBootstrap = message.payload;
    return;
  }

  if (message.type === 'native.startRun') handlers?.onStartRun?.(message.payload.run);
  else if (message.type === 'native.pause') handlers?.onPause?.();
  else if (message.type === 'native.resume') handlers?.onResume?.();
  else if (message.type === 'native.settingsUpdated') handlers?.onSettingsUpdated?.(message.payload.settings);
  else if (message.type === 'native.entitlementsUpdated') handlers?.onEntitlementsUpdated?.(message.payload.entitlements);
  else if (message.type === 'native.scoreResult') handlers?.onScoreResult?.(message.payload);
  else if (message.type === 'native.error') console.warn('[Brewmancer bridge] native.error', message.payload.message);
}

function post(message: WebToNativeMessage): void {
  const bridge = getWindow()?.ReactNativeWebView;
  if (typeof bridge?.postMessage !== 'function') return;
  bridge.postMessage(JSON.stringify(message));
}

export function isNativeShell(): boolean {
  return typeof getWindow()?.ReactNativeWebView?.postMessage === 'function';
}

export function initNativeBridge(options: { debug: boolean; hiddenVariant: boolean }, nextHandlers: NativeBridgeHandlers): void {
  handlers = nextHandlers;
  if (initialized) {
    if (queuedBootstrap !== null) {
      const bootstrap = queuedBootstrap;
      queuedBootstrap = null;
      handlers.onBootstrap?.(bootstrap);
    }
    return;
  }

  initialized = true;
  const bridgeWindow = getWindow();
  if (bridgeWindow === null || typeof document === 'undefined') return;

  bridgeWindow.__BREWMANCER_NATIVE_BRIDGE__ = { receive: receiveNativeMessage };
  window.addEventListener('message', (event) => receiveNativeMessage(event.data as NativeToWebMessage | string));
  document.addEventListener('message', (event) => receiveNativeMessage((event as MessageEvent).data as NativeToWebMessage | string));

  if (queuedBootstrap !== null) {
    const bootstrap = queuedBootstrap;
    queuedBootstrap = null;
    handlers.onBootstrap?.(bootstrap);
  }

  post({
    version: BRIDGE_VERSION,
    type: 'web.ready',
    payload: {
      href: window.location.href,
      debug: options.debug,
      hiddenVariant: options.hiddenVariant,
    },
  });
}

export function sendWebError(error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;
  post({
    version: BRIDGE_VERSION,
    type: 'web.error',
    payload: {
      message,
      ...(stack ? { stack } : {}),
    },
  });
}

export function announceBridgeRun(run: RunConfig, flags: RunFlags): void {
  activeRun = run;
  post({
    version: BRIDGE_VERSION,
    type: 'game.started',
    payload: { run, flags },
  });
}

export function announceBridgePaused(): void {
  post({ version: BRIDGE_VERSION, type: 'game.paused', payload: {} });
}

export function sendGameOver(options: { world: World; debug: boolean; hiddenVariant: boolean }): void {
  if (activeRun === null) return;
  if (activeRun.levelId !== options.world.levelDefinition.id) {
    sendWebError(`Run level mismatch: native=${activeRun.levelId}, world=${options.world.levelDefinition.id}`);
    return;
  }
  post({
    version: BRIDGE_VERSION,
    type: 'game.over',
    payload: {
      runId: activeRun.runId,
      score: options.world.score(),
      seconds: options.world.elapsedSec,
      level: options.world.level,
      levelId: activeRun.levelId,
      difficulty: activeRun.difficulty,
      victory: options.world.victory,
      bossKills: options.world.bossKills,
      playerLevel: options.world.level,
      seed: activeRun.seed,
      debug: options.debug,
      allAbilities: options.world.allAbilities,
      hiddenVariant: options.hiddenVariant,
    },
  });
}

export function requestNativeRestart(): void {
  if (activeRun === null) return;
  post({ version: BRIDGE_VERSION, type: 'game.requestRestart', payload: { runId: activeRun.runId } });
}

export function requestNativeExit(): void {
  if (activeRun === null) return;
  post({ version: BRIDGE_VERSION, type: 'game.requestExit', payload: { runId: activeRun.runId } });
}

export function requestNativeLeaderboard(): void {
  post({ version: BRIDGE_VERSION, type: 'game.requestLeaderboard', payload: {} });
}

export function requestNativeScoreShare(data: Pick<ScoreSharePayload, 'score' | 'seconds' | 'playerLevel' | 'victory'>): void {
  if (activeRun === null) return;
  post({
    version: BRIDGE_VERSION,
    type: 'game.shareScore',
    payload: {
      runId: activeRun.runId,
      levelId: activeRun.levelId,
      ...data,
    },
  });
}

export function requestNativeHaptic(event: GameHapticEvent): void {
  post({ version: BRIDGE_VERSION, type: 'game.haptic', payload: { event } });
}

export function requestNativeSettingsUpdate(settings: NativeSettings): void {
  post({
    version: BRIDGE_VERSION,
    type: 'game.requestSettingsUpdate',
    payload: { settings },
  });
}
